import { useState } from "react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/base/dialog";
import { Button } from "@/components/ui/base/button";
import type { MerchantsRepository } from "@/application/merchants/merchants_repository";

interface Props {
  businessProfileId: string;
  currentEmail?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEmailChanged: (newEmail: string) => void;
  repo: MerchantsRepository;
}

type CascadeResult = {
  success?: boolean | null;
  cognitoUpdated?: boolean | null;
  userProfileUpdated?: boolean | null;
  provisioningUpdated?: number | null;
  stripeUpdated?: boolean | null;
  errors?: (string | null)[] | null;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function StatusRow({
  label,
  ok,
  detail,
}: {
  label: string;
  ok: boolean;
  detail?: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
      ) : (
        <XCircle className="h-4 w-4 shrink-0 text-red-500" />
      )}
      <span className="text-foreground">{label}</span>
      {detail && <span className="text-muted-foreground">{detail}</span>}
    </div>
  );
}

export function ChangeLoginEmailDialog({
  businessProfileId,
  currentEmail,
  open,
  onOpenChange,
  onEmailChanged,
  repo,
}: Props) {
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CascadeResult | null>(null);

  const isValidEmail = EMAIL_RE.test(newEmail.trim());

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && submitting) return; // don't allow dismiss during an in-flight change
    if (!nextOpen) {
      setNewEmail("");
      setResult(null);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit() {
    if (!isValidEmail || submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const data = await repo.changeLoginEmail(
        businessProfileId,
        newEmail.trim(),
      );
      setResult(data);

      const nonFatalErrors = data.errors?.filter(Boolean) ?? [];
      const nothingUpdated =
        !data.cognitoUpdated &&
        !data.userProfileUpdated &&
        !data.provisioningUpdated &&
        !data.stripeUpdated;
      const isNoOp =
        data.success === true &&
        nothingUpdated &&
        nonFatalErrors.some((e) => e?.toLowerCase().includes("unchanged"));

      if (isNoOp) {
        // Same-email no-op — nothing changed, just close
        toast("Email unchanged — no changes applied");
        handleOpenChange(false);
      } else if (data.success && nonFatalErrors.length === 0) {
        // Full success — close dialog
        toast.success("Login email updated across all systems");
        onEmailChanged(newEmail.trim());
        handleOpenChange(false);
      } else if (data.success && nonFatalErrors.length > 0) {
        // Partial success (e.g. non-fatal Stripe failure) — keep dialog open for review
        toast(`Email updated with warnings: ${nonFatalErrors.join(", ")}`, {
          icon: "⚠️",
        });
        onEmailChanged(newEmail.trim());
      } else {
        // Hard failure — keep dialog open for review/retry
        toast.error(nonFatalErrors.join(", ") || "Email change failed");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change login email</DialogTitle>
          <DialogDescription>
            Updates Cognito login, User Profile, Provisioning records, and
            Stripe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {currentEmail && (
            <div>
              <p className="text-muted-foreground text-xs">Current email</p>
              <p className="text-foreground text-sm font-medium">
                {currentEmail}
              </p>
            </div>
          )}

          <div>
            <label
              htmlFor="new-email"
              className="text-foreground mb-1 block text-xs font-medium"
            >
              New email
            </label>
            <input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                setResult(null);
              }}
              placeholder="merchant@example.com"
              disabled={submitting}
              className="border-border w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
            />
          </div>

          {result && (
            <div className="bg-muted space-y-1.5 rounded-md border p-3">
              <p className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                Cascade result
              </p>
              <StatusRow label="Cognito" ok={result.cognitoUpdated === true} />
              <StatusRow
                label="User Profile"
                ok={result.userProfileUpdated === true}
              />
              <StatusRow
                label="Provisioning"
                ok={(result.provisioningUpdated ?? 0) > 0}
                detail={
                  result.provisioningUpdated != null
                    ? `${result.provisioningUpdated} updated`
                    : undefined
                }
              />
              <StatusRow label="Stripe" ok={result.stripeUpdated === true} />
              {result.errors && result.errors.length > 0 && (
                <p className="mt-2 text-xs text-red-600">
                  {result.errors.filter(Boolean).join(", ")}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            {result ? "Close" : "Cancel"}
          </Button>
          {result ? (
            // Non-full-success: let admin edit email and retry in place
            <Button onClick={() => setResult(null)} disabled={submitting}>
              Try again
            </Button>
          ) : (
            <Button
              onClick={() => void handleSubmit()}
              disabled={!isValidEmail || submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
